import { useAuth } from '@/features/auth/components/AuthListener'
import GoogleIcon from '@/assets/icons/auth/google-logo.svg'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'
import { signInWithGoogle, auth } from '@/features/auth/services/firebaseAuth'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'

import React, { useEffect, useState } from 'react'

export function LoginForm({ className, ...props }: React.ComponentProps<'form'>) {
    const router = useNavigate()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [isMounted, setIsMounted] = useState(false)

    const { user, loading: loadingUser } = useAuth()
    useEffect(() => {
        if (!loadingUser && user) {
            router('/dashboard')
        }
    }, [user, loadingUser])
    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            await signInWithEmailAndPassword(auth, email, password)
            setError('User signin successfully!')
        } catch (error: any) {
            console.error('Login error:', error)

            switch (error.code) {
                case 'auth/invalid-email':
                    setError('Invalid email address')
                    break
                case 'auth/user-disabled':
                    setError('Account disabled')
                    break
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                    setError('Invalid email or password')
                    break
                case 'auth/too-many-requests':
                    setError('Too many attempts. Try again later')
                    break
                default:
                    setError('Login failed. Please try again')
            }
        } finally {
            if (isMounted) setLoading(false)
        }
    }
    return (
        <form onSubmit={handleSignIn} className={cn('flex flex-col gap-6', className)} {...props}>
            <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Welcome Back, Storyteller</h1>
                <p className="text-muted-foreground text-sm text-balance">
                    Enter your email below to login to your account
                </p>
            </div>
            {error && (
                <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded-md">
                    {error}
                </div>
            )}
            <div className="grid gap-6">
                <div className="grid gap-3">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="grid gap-3">
                    <div className="flex items-center">
                        <Label htmlFor="password">Password</Label>
                        <a
                            href="/auth/forgotpassword"
                            className="ml-auto text-sm underline-offset-4 hover:underline"
                        >
                            Forgot your password?
                        </a>
                    </div>
                    <Input
                        id="password"
                        type="password"
                        value={password}
                        placeholder="Enter your password"
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>
                <Button type="submit" className="w-full bg-primary">
                    {loading ? 'Signing in....' : 'Login'}
                </Button>
                <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                    <span className="bg-background text-muted-foreground relative z-10 px-2">
                        Or continue with
                    </span>
                </div>
                <Button onClick={signInWithGoogle} variant="outline" className="w-full">
                    {/* <<GoogleIcon className={`w-12 h-12`} /> */}
                    <img src={GoogleIcon} alt="Google Logo" className="w-4 h-4 mr-2" />
                    Login with Google
                </Button>
            </div>
            <div className="text-center text-sm">
                Don&apos;t have an account?{' '}
                <a href="/register" className="underline underline-offset-4">
                    Register
                </a>
            </div>
        </form>
    )
}
